// #431 / #441: US airport dataset + nearest-airport suggestion for the out-of-
// state shipping picker. The picker is searchable by IATA / city / name, and
// the portal SUGGESTS a nearest airport from the client's ZIP (then state) - the
// suggestion is NEVER auto-applied; the client confirms it (#431).
//
// #441: widened from ~80 major hubs to a comprehensive commercial/cargo set
// (all metros + significant regional airports) so a small-city client always
// finds a reachable airport in search, AND the ZIP -> nearest suggestion is
// resolved from contiguous ZIP3 bands covering the entire US (so e.g. San
// Antonio -> SAT not DFW, Ontario CA -> ONT not LAX, Naples FL -> RSW not MIA).
// The state primary remains a guaranteed non-null backstop for any ZIP a band
// misses.

export type UsAirport = {
  iata: string; // 3-letter code
  name: string;
  city: string;
  state: string; // 2-letter USPS code
};

export const US_AIRPORTS: readonly UsAirport[] = [
  // Alabama
  { iata: "BHM", name: "Birmingham-Shuttlesworth International Airport", city: "Birmingham", state: "AL" },
  { iata: "HSV", name: "Huntsville International Airport", city: "Huntsville", state: "AL" },
  { iata: "MOB", name: "Mobile Regional Airport", city: "Mobile", state: "AL" },
  { iata: "MGM", name: "Montgomery Regional Airport", city: "Montgomery", state: "AL" },
  { iata: "DHN", name: "Dothan Regional Airport", city: "Dothan", state: "AL" },
  // Alaska
  { iata: "ANC", name: "Ted Stevens Anchorage International Airport", city: "Anchorage", state: "AK" },
  { iata: "FAI", name: "Fairbanks International Airport", city: "Fairbanks", state: "AK" },
  { iata: "JNU", name: "Juneau International Airport", city: "Juneau", state: "AK" },
  // Arizona
  { iata: "PHX", name: "Phoenix Sky Harbor International Airport", city: "Phoenix", state: "AZ" },
  { iata: "AZA", name: "Phoenix-Mesa Gateway Airport", city: "Mesa", state: "AZ" },
  { iata: "TUS", name: "Tucson International Airport", city: "Tucson", state: "AZ" },
  { iata: "FLG", name: "Flagstaff Pulliam Airport", city: "Flagstaff", state: "AZ" },
  { iata: "YUM", name: "Yuma International Airport", city: "Yuma", state: "AZ" },
  { iata: "PRC", name: "Prescott Regional Airport", city: "Prescott", state: "AZ" },
  // Arkansas
  { iata: "LIT", name: "Bill and Hillary Clinton National Airport", city: "Little Rock", state: "AR" },
  { iata: "XNA", name: "Northwest Arkansas National Airport", city: "Bentonville", state: "AR" },
  { iata: "FSM", name: "Fort Smith Regional Airport", city: "Fort Smith", state: "AR" },
  { iata: "TXK", name: "Texarkana Regional Airport", city: "Texarkana", state: "AR" },
  // California
  { iata: "LAX", name: "Los Angeles International Airport", city: "Los Angeles", state: "CA" },
  { iata: "SFO", name: "San Francisco International Airport", city: "San Francisco", state: "CA" },
  { iata: "SAN", name: "San Diego International Airport", city: "San Diego", state: "CA" },
  { iata: "SMF", name: "Sacramento International Airport", city: "Sacramento", state: "CA" },
  { iata: "SJC", name: "Norman Y. Mineta San Jose International Airport", city: "San Jose", state: "CA" },
  { iata: "OAK", name: "Oakland International Airport", city: "Oakland", state: "CA" },
  { iata: "ONT", name: "Ontario International Airport", city: "Ontario", state: "CA" },
  { iata: "SNA", name: "John Wayne Airport", city: "Santa Ana", state: "CA" },
  { iata: "BUR", name: "Hollywood Burbank Airport", city: "Burbank", state: "CA" },
  { iata: "FAT", name: "Fresno Yosemite International Airport", city: "Fresno", state: "CA" },
  { iata: "PSP", name: "Palm Springs International Airport", city: "Palm Springs", state: "CA" },
  { iata: "SBA", name: "Santa Barbara Municipal Airport", city: "Santa Barbara", state: "CA" },
  { iata: "MRY", name: "Monterey Regional Airport", city: "Monterey", state: "CA" },
  { iata: "BFL", name: "Meadows Field Airport", city: "Bakersfield", state: "CA" },
  { iata: "ACV", name: "California Redwood Coast-Humboldt County Airport", city: "Arcata", state: "CA" },
  { iata: "RDD", name: "Redding Regional Airport", city: "Redding", state: "CA" },
  { iata: "LGB", name: "Long Beach Airport", city: "Long Beach", state: "CA" },
  { iata: "SBP", name: "San Luis Obispo County Regional Airport", city: "San Luis Obispo", state: "CA" },
  // Colorado
  { iata: "DEN", name: "Denver International Airport", city: "Denver", state: "CO" },
  { iata: "COS", name: "Colorado Springs Airport", city: "Colorado Springs", state: "CO" },
  { iata: "GJT", name: "Grand Junction Regional Airport", city: "Grand Junction", state: "CO" },
  { iata: "DRO", name: "Durango-La Plata County Airport", city: "Durango", state: "CO" },
  { iata: "MTJ", name: "Montrose Regional Airport", city: "Montrose", state: "CO" },
  { iata: "ASE", name: "Aspen-Pitkin County Airport", city: "Aspen", state: "CO" },
  { iata: "EGE", name: "Eagle County Regional Airport", city: "Eagle", state: "CO" },
  { iata: "HDN", name: "Yampa Valley Airport", city: "Hayden", state: "CO" },
  { iata: "PUB", name: "Pueblo Memorial Airport", city: "Pueblo", state: "CO" },
  // Connecticut
  { iata: "BDL", name: "Bradley International Airport", city: "Windsor Locks", state: "CT" },
  { iata: "HVN", name: "Tweed New Haven Airport", city: "New Haven", state: "CT" },
  // Delaware
  { iata: "ILG", name: "Wilmington Airport", city: "Wilmington", state: "DE" },
  // District of Columbia / DC-area
  { iata: "DCA", name: "Ronald Reagan Washington National Airport", city: "Washington", state: "DC" },
  { iata: "IAD", name: "Washington Dulles International Airport", city: "Dulles", state: "VA" },
  // Florida
  { iata: "MIA", name: "Miami International Airport", city: "Miami", state: "FL" },
  { iata: "MCO", name: "Orlando International Airport", city: "Orlando", state: "FL" },
  { iata: "TPA", name: "Tampa International Airport", city: "Tampa", state: "FL" },
  { iata: "FLL", name: "Fort Lauderdale-Hollywood International Airport", city: "Fort Lauderdale", state: "FL" },
  { iata: "JAX", name: "Jacksonville International Airport", city: "Jacksonville", state: "FL" },
  { iata: "RSW", name: "Southwest Florida International Airport", city: "Fort Myers", state: "FL" },
  { iata: "SRQ", name: "Sarasota-Bradenton International Airport", city: "Sarasota", state: "FL" },
  { iata: "PBI", name: "Palm Beach International Airport", city: "West Palm Beach", state: "FL" },
  { iata: "PNS", name: "Pensacola International Airport", city: "Pensacola", state: "FL" },
  { iata: "TLH", name: "Tallahassee International Airport", city: "Tallahassee", state: "FL" },
  { iata: "GNV", name: "Gainesville Regional Airport", city: "Gainesville", state: "FL" },
  { iata: "VPS", name: "Destin-Fort Walton Beach Airport", city: "Valparaiso", state: "FL" },
  { iata: "PGD", name: "Punta Gorda Airport", city: "Punta Gorda", state: "FL" },
  { iata: "ECP", name: "Northwest Florida Beaches International Airport", city: "Panama City", state: "FL" },
  { iata: "DAB", name: "Daytona Beach International Airport", city: "Daytona Beach", state: "FL" },
  { iata: "MLB", name: "Melbourne Orlando International Airport", city: "Melbourne", state: "FL" },
  { iata: "EYW", name: "Key West International Airport", city: "Key West", state: "FL" },
  // Georgia
  { iata: "ATL", name: "Hartsfield-Jackson Atlanta International Airport", city: "Atlanta", state: "GA" },
  { iata: "SAV", name: "Savannah/Hilton Head International Airport", city: "Savannah", state: "GA" },
  { iata: "AGS", name: "Augusta Regional Airport", city: "Augusta", state: "GA" },
  { iata: "CSG", name: "Columbus Metropolitan Airport", city: "Columbus", state: "GA" },
  { iata: "VLD", name: "Valdosta Regional Airport", city: "Valdosta", state: "GA" },
  { iata: "ABY", name: "Southwest Georgia Regional Airport", city: "Albany", state: "GA" },
  // Hawaii
  { iata: "HNL", name: "Daniel K. Inouye International Airport", city: "Honolulu", state: "HI" },
  { iata: "OGG", name: "Kahului Airport", city: "Kahului", state: "HI" },
  { iata: "KOA", name: "Ellison Onizuka Kona International Airport", city: "Kailua-Kona", state: "HI" },
  { iata: "LIH", name: "Lihue Airport", city: "Lihue", state: "HI" },
  { iata: "ITO", name: "Hilo International Airport", city: "Hilo", state: "HI" },
  // Idaho
  { iata: "BOI", name: "Boise Airport", city: "Boise", state: "ID" },
  { iata: "IDA", name: "Idaho Falls Regional Airport", city: "Idaho Falls", state: "ID" },
  { iata: "SUN", name: "Friedman Memorial Airport", city: "Hailey", state: "ID" },
  { iata: "LWS", name: "Lewiston-Nez Perce County Airport", city: "Lewiston", state: "ID" },
  { iata: "PIH", name: "Pocatello Regional Airport", city: "Pocatello", state: "ID" },
  // Illinois
  { iata: "ORD", name: "O'Hare International Airport", city: "Chicago", state: "IL" },
  { iata: "MDW", name: "Chicago Midway International Airport", city: "Chicago", state: "IL" },
  { iata: "PIA", name: "General Wayne A. Downing Peoria International Airport", city: "Peoria", state: "IL" },
  { iata: "MLI", name: "Quad Cities International Airport", city: "Moline", state: "IL" },
  { iata: "SPI", name: "Abraham Lincoln Capital Airport", city: "Springfield", state: "IL" },
  { iata: "BMI", name: "Central Illinois Regional Airport", city: "Bloomington", state: "IL" },
  { iata: "CMI", name: "University of Illinois Willard Airport", city: "Champaign", state: "IL" },
  // Indiana
  { iata: "IND", name: "Indianapolis International Airport", city: "Indianapolis", state: "IN" },
  { iata: "FWA", name: "Fort Wayne International Airport", city: "Fort Wayne", state: "IN" },
  { iata: "SBN", name: "South Bend International Airport", city: "South Bend", state: "IN" },
  { iata: "EVV", name: "Evansville Regional Airport", city: "Evansville", state: "IN" },
  // Iowa
  { iata: "DSM", name: "Des Moines International Airport", city: "Des Moines", state: "IA" },
  { iata: "CID", name: "The Eastern Iowa Airport", city: "Cedar Rapids", state: "IA" },
  { iata: "DBQ", name: "Dubuque Regional Airport", city: "Dubuque", state: "IA" },
  { iata: "SUX", name: "Sioux Gateway Airport", city: "Sioux City", state: "IA" },
  // Kansas
  { iata: "ICT", name: "Wichita Dwight D. Eisenhower National Airport", city: "Wichita", state: "KS" },
  { iata: "MHK", name: "Manhattan Regional Airport", city: "Manhattan", state: "KS" },
  { iata: "GCK", name: "Garden City Regional Airport", city: "Garden City", state: "KS" },
  // Kentucky
  { iata: "SDF", name: "Louisville Muhammad Ali International Airport", city: "Louisville", state: "KY" },
  { iata: "LEX", name: "Blue Grass Airport", city: "Lexington", state: "KY" },
  { iata: "OWB", name: "Owensboro-Daviess County Airport", city: "Owensboro", state: "KY" },
  { iata: "PAH", name: "Barkley Regional Airport", city: "Paducah", state: "KY" },
  // Louisiana
  { iata: "MSY", name: "Louis Armstrong New Orleans International Airport", city: "New Orleans", state: "LA" },
  { iata: "SHV", name: "Shreveport Regional Airport", city: "Shreveport", state: "LA" },
  { iata: "BTR", name: "Baton Rouge Metropolitan Airport", city: "Baton Rouge", state: "LA" },
  { iata: "LFT", name: "Lafayette Regional Airport", city: "Lafayette", state: "LA" },
  { iata: "LCH", name: "Lake Charles Regional Airport", city: "Lake Charles", state: "LA" },
  { iata: "AEX", name: "Alexandria International Airport", city: "Alexandria", state: "LA" },
  { iata: "MLU", name: "Monroe Regional Airport", city: "Monroe", state: "LA" },
  // Maine
  { iata: "PWM", name: "Portland International Jetport", city: "Portland", state: "ME" },
  { iata: "BGR", name: "Bangor International Airport", city: "Bangor", state: "ME" },
  // Maryland
  { iata: "BWI", name: "Baltimore/Washington International Thurgood Marshall Airport", city: "Baltimore", state: "MD" },
  { iata: "SBY", name: "Salisbury-Ocean City Wicomico Regional Airport", city: "Salisbury", state: "MD" },
  // Massachusetts
  { iata: "BOS", name: "Logan International Airport", city: "Boston", state: "MA" },
  { iata: "ACK", name: "Nantucket Memorial Airport", city: "Nantucket", state: "MA" },
  { iata: "HYA", name: "Cape Cod Gateway Airport", city: "Hyannis", state: "MA" },
  // Michigan
  { iata: "DTW", name: "Detroit Metropolitan Wayne County Airport", city: "Detroit", state: "MI" },
  { iata: "GRR", name: "Gerald R. Ford International Airport", city: "Grand Rapids", state: "MI" },
  { iata: "FNT", name: "Bishop International Airport", city: "Flint", state: "MI" },
  { iata: "LAN", name: "Capital Region International Airport", city: "Lansing", state: "MI" },
  { iata: "AZO", name: "Kalamazoo/Battle Creek International Airport", city: "Kalamazoo", state: "MI" },
  { iata: "TVC", name: "Cherry Capital Airport", city: "Traverse City", state: "MI" },
  { iata: "MBS", name: "MBS International Airport", city: "Saginaw", state: "MI" },
  { iata: "MQT", name: "Sawyer International Airport", city: "Marquette", state: "MI" },
  // Minnesota
  { iata: "MSP", name: "Minneapolis-Saint Paul International Airport", city: "Minneapolis", state: "MN" },
  { iata: "DLH", name: "Duluth International Airport", city: "Duluth", state: "MN" },
  { iata: "RST", name: "Rochester International Airport", city: "Rochester", state: "MN" },
  // Mississippi
  { iata: "JAN", name: "Jackson-Medgar Wiley Evers International Airport", city: "Jackson", state: "MS" },
  { iata: "GPT", name: "Gulfport-Biloxi International Airport", city: "Gulfport", state: "MS" },
  // Missouri
  { iata: "MCI", name: "Kansas City International Airport", city: "Kansas City", state: "MO" },
  { iata: "STL", name: "St. Louis Lambert International Airport", city: "St. Louis", state: "MO" },
  { iata: "COU", name: "Columbia Regional Airport", city: "Columbia", state: "MO" },
  { iata: "SGF", name: "Springfield-Branson National Airport", city: "Springfield", state: "MO" },
  { iata: "JLN", name: "Joplin Regional Airport", city: "Joplin", state: "MO" },
  // Montana
  { iata: "BIL", name: "Billings Logan International Airport", city: "Billings", state: "MT" },
  { iata: "BZN", name: "Bozeman Yellowstone International Airport", city: "Bozeman", state: "MT" },
  { iata: "MSO", name: "Missoula Montana Airport", city: "Missoula", state: "MT" },
  { iata: "GTF", name: "Great Falls International Airport", city: "Great Falls", state: "MT" },
  { iata: "HLN", name: "Helena Regional Airport", city: "Helena", state: "MT" },
  { iata: "FCA", name: "Glacier Park International Airport", city: "Kalispell", state: "MT" },
  // Nebraska
  { iata: "OMA", name: "Eppley Airfield", city: "Omaha", state: "NE" },
  { iata: "LNK", name: "Lincoln Airport", city: "Lincoln", state: "NE" },
  { iata: "GRI", name: "Central Nebraska Regional Airport", city: "Grand Island", state: "NE" },
  // Nevada
  { iata: "LAS", name: "Harry Reid International Airport", city: "Las Vegas", state: "NV" },
  { iata: "RNO", name: "Reno-Tahoe International Airport", city: "Reno", state: "NV" },
  { iata: "EKO", name: "Elko Regional Airport", city: "Elko", state: "NV" },
  // New Hampshire
  { iata: "MHT", name: "Manchester-Boston Regional Airport", city: "Manchester", state: "NH" },
  { iata: "LEB", name: "Lebanon Municipal Airport", city: "Lebanon", state: "NH" },
  // New Jersey
  { iata: "EWR", name: "Newark Liberty International Airport", city: "Newark", state: "NJ" },
  { iata: "ACY", name: "Atlantic City International Airport", city: "Atlantic City", state: "NJ" },
  { iata: "TTN", name: "Trenton-Mercer Airport", city: "Trenton", state: "NJ" },
  // New Mexico
  { iata: "ABQ", name: "Albuquerque International Sunport", city: "Albuquerque", state: "NM" },
  { iata: "ROW", name: "Roswell Air Center", city: "Roswell", state: "NM" },
  { iata: "SAF", name: "Santa Fe Regional Airport", city: "Santa Fe", state: "NM" },
  { iata: "FMN", name: "Four Corners Regional Airport", city: "Farmington", state: "NM" },
  // New York
  { iata: "JFK", name: "John F. Kennedy International Airport", city: "New York", state: "NY" },
  { iata: "LGA", name: "LaGuardia Airport", city: "New York", state: "NY" },
  { iata: "BUF", name: "Buffalo Niagara International Airport", city: "Buffalo", state: "NY" },
  { iata: "ROC", name: "Frederick Douglass Greater Rochester International Airport", city: "Rochester", state: "NY" },
  { iata: "SYR", name: "Syracuse Hancock International Airport", city: "Syracuse", state: "NY" },
  { iata: "ALB", name: "Albany International Airport", city: "Albany", state: "NY" },
  { iata: "ISP", name: "Long Island MacArthur Airport", city: "Islip", state: "NY" },
  { iata: "ELM", name: "Elmira Corning Regional Airport", city: "Elmira", state: "NY" },
  { iata: "BGM", name: "Greater Binghamton Airport", city: "Binghamton", state: "NY" },
  { iata: "HPN", name: "Westchester County Airport", city: "White Plains", state: "NY" },
  { iata: "SWF", name: "New York Stewart International Airport", city: "Newburgh", state: "NY" },
  { iata: "IAG", name: "Niagara Falls International Airport", city: "Niagara Falls", state: "NY" },
  { iata: "ITH", name: "Ithaca Tompkins International Airport", city: "Ithaca", state: "NY" },
  // North Carolina
  { iata: "CLT", name: "Charlotte Douglas International Airport", city: "Charlotte", state: "NC" },
  { iata: "RDU", name: "Raleigh-Durham International Airport", city: "Raleigh", state: "NC" },
  { iata: "GSO", name: "Piedmont Triad International Airport", city: "Greensboro", state: "NC" },
  { iata: "AVL", name: "Asheville Regional Airport", city: "Asheville", state: "NC" },
  { iata: "ILM", name: "Wilmington International Airport", city: "Wilmington", state: "NC" },
  { iata: "FAY", name: "Fayetteville Regional Airport", city: "Fayetteville", state: "NC" },
  { iata: "OAJ", name: "Albert J. Ellis Airport", city: "Jacksonville", state: "NC" },
  { iata: "EWN", name: "Coastal Carolina Regional Airport", city: "New Bern", state: "NC" },
  // North Dakota
  { iata: "FAR", name: "Hector International Airport", city: "Fargo", state: "ND" },
  { iata: "BIS", name: "Bismarck Municipal Airport", city: "Bismarck", state: "ND" },
  { iata: "MOT", name: "Minot International Airport", city: "Minot", state: "ND" },
  { iata: "GFK", name: "Grand Forks International Airport", city: "Grand Forks", state: "ND" },
  { iata: "ISN", name: "Sloulin Field International Airport", city: "Williston", state: "ND" },
  // Ohio
  { iata: "CMH", name: "John Glenn Columbus International Airport", city: "Columbus", state: "OH" },
  { iata: "CLE", name: "Cleveland Hopkins International Airport", city: "Cleveland", state: "OH" },
  { iata: "CVG", name: "Cincinnati/Northern Kentucky International Airport", city: "Cincinnati", state: "OH" },
  { iata: "DAY", name: "Dayton International Airport", city: "Dayton", state: "OH" },
  { iata: "TOL", name: "Eugene F. Kranz Toledo Express Airport", city: "Toledo", state: "OH" },
  { iata: "CAK", name: "Akron-Canton Airport", city: "Akron", state: "OH" },
  // Oklahoma
  { iata: "OKC", name: "Will Rogers World Airport", city: "Oklahoma City", state: "OK" },
  { iata: "TUL", name: "Tulsa International Airport", city: "Tulsa", state: "OK" },
  { iata: "LAW", name: "Lawton-Fort Sill Regional Airport", city: "Lawton", state: "OK" },
  // Oregon
  { iata: "PDX", name: "Portland International Airport", city: "Portland", state: "OR" },
  { iata: "RDM", name: "Roberts Field", city: "Redmond", state: "OR" },
  { iata: "EUG", name: "Eugene Airport", city: "Eugene", state: "OR" },
  { iata: "MFR", name: "Rogue Valley International-Medford Airport", city: "Medford", state: "OR" },
  { iata: "OTH", name: "Southwest Oregon Regional Airport", city: "North Bend", state: "OR" },
  { iata: "LMT", name: "Crater Lake-Klamath Regional Airport", city: "Klamath Falls", state: "OR" },
  // Pennsylvania
  { iata: "PHL", name: "Philadelphia International Airport", city: "Philadelphia", state: "PA" },
  { iata: "PIT", name: "Pittsburgh International Airport", city: "Pittsburgh", state: "PA" },
  { iata: "ABE", name: "Lehigh Valley International Airport", city: "Allentown", state: "PA" },
  { iata: "MDT", name: "Harrisburg International Airport", city: "Harrisburg", state: "PA" },
  { iata: "AVP", name: "Wilkes-Barre/Scranton International Airport", city: "Avoca", state: "PA" },
  { iata: "ERI", name: "Erie International Airport", city: "Erie", state: "PA" },
  { iata: "UNV", name: "University Park Airport", city: "State College", state: "PA" },
  // Rhode Island
  { iata: "PVD", name: "Rhode Island T. F. Green International Airport", city: "Providence", state: "RI" },
  // South Carolina
  { iata: "CHS", name: "Charleston International Airport", city: "Charleston", state: "SC" },
  { iata: "GSP", name: "Greenville-Spartanburg International Airport", city: "Greer", state: "SC" },
  { iata: "CAE", name: "Columbia Metropolitan Airport", city: "Columbia", state: "SC" },
  { iata: "MYR", name: "Myrtle Beach International Airport", city: "Myrtle Beach", state: "SC" },
  { iata: "FLO", name: "Florence Regional Airport", city: "Florence", state: "SC" },
  { iata: "HHH", name: "Hilton Head Airport", city: "Hilton Head Island", state: "SC" },
  // South Dakota
  { iata: "FSD", name: "Sioux Falls Regional Airport", city: "Sioux Falls", state: "SD" },
  { iata: "RAP", name: "Rapid City Regional Airport", city: "Rapid City", state: "SD" },
  { iata: "PIR", name: "Pierre Regional Airport", city: "Pierre", state: "SD" },
  // Tennessee
  { iata: "BNA", name: "Nashville International Airport", city: "Nashville", state: "TN" },
  { iata: "MEM", name: "Memphis International Airport", city: "Memphis", state: "TN" },
  { iata: "TYS", name: "McGhee Tyson Airport", city: "Knoxville", state: "TN" },
  { iata: "CHA", name: "Chattanooga Metropolitan Airport", city: "Chattanooga", state: "TN" },
  { iata: "TRI", name: "Tri-Cities Airport", city: "Blountville", state: "TN" },
  // Texas
  { iata: "DFW", name: "Dallas/Fort Worth International Airport", city: "Dallas", state: "TX" },
  { iata: "IAH", name: "George Bush Intercontinental Airport", city: "Houston", state: "TX" },
  { iata: "DAL", name: "Dallas Love Field", city: "Dallas", state: "TX" },
  { iata: "HOU", name: "William P. Hobby Airport", city: "Houston", state: "TX" },
  { iata: "AUS", name: "Austin-Bergstrom International Airport", city: "Austin", state: "TX" },
  { iata: "SAT", name: "San Antonio International Airport", city: "San Antonio", state: "TX" },
  { iata: "ELP", name: "El Paso International Airport", city: "El Paso", state: "TX" },
  { iata: "MAF", name: "Midland International Air and Space Port", city: "Midland", state: "TX" },
  { iata: "LBB", name: "Lubbock Preston Smith International Airport", city: "Lubbock", state: "TX" },
  { iata: "AMA", name: "Rick Husband Amarillo International Airport", city: "Amarillo", state: "TX" },
  { iata: "SJT", name: "San Angelo Regional Airport", city: "San Angelo", state: "TX" },
  { iata: "ABI", name: "Abilene Regional Airport", city: "Abilene", state: "TX" },
  { iata: "ACT", name: "Waco Regional Airport", city: "Waco", state: "TX" },
  { iata: "GGG", name: "East Texas Regional Airport", city: "Longview", state: "TX" },
  { iata: "TYR", name: "Tyler Pounds Regional Airport", city: "Tyler", state: "TX" },
  { iata: "CLL", name: "Easterwood Airport", city: "College Station", state: "TX" },
  { iata: "BRO", name: "Brownsville/South Padre Island International Airport", city: "Brownsville", state: "TX" },
  { iata: "HRL", name: "Valley International Airport", city: "Harlingen", state: "TX" },
  { iata: "MFE", name: "McAllen International Airport", city: "McAllen", state: "TX" },
  { iata: "LRD", name: "Laredo International Airport", city: "Laredo", state: "TX" },
  { iata: "CRP", name: "Corpus Christi International Airport", city: "Corpus Christi", state: "TX" },
  { iata: "VCT", name: "Victoria Regional Airport", city: "Victoria", state: "TX" },
  // Utah
  { iata: "SLC", name: "Salt Lake City International Airport", city: "Salt Lake City", state: "UT" },
  { iata: "OGD", name: "Ogden-Hinckley Airport", city: "Ogden", state: "UT" },
  { iata: "PVU", name: "Provo Municipal Airport", city: "Provo", state: "UT" },
  { iata: "SGU", name: "St. George Regional Airport", city: "St. George", state: "UT" },
  { iata: "CNY", name: "Canyonlands Regional Airport", city: "Moab", state: "UT" },
  { iata: "VEL", name: "Vernal Regional Airport", city: "Vernal", state: "UT" },
  // Vermont
  { iata: "BTV", name: "Burlington International Airport", city: "Burlington", state: "VT" },
  // Virginia
  { iata: "RIC", name: "Richmond International Airport", city: "Richmond", state: "VA" },
  { iata: "ORF", name: "Norfolk International Airport", city: "Norfolk", state: "VA" },
  { iata: "ROA", name: "Roanoke-Blacksburg Regional Airport", city: "Roanoke", state: "VA" },
  { iata: "CHO", name: "Charlottesville-Albemarle Airport", city: "Charlottesville", state: "VA" },
  { iata: "PHF", name: "Newport News/Williamsburg International Airport", city: "Newport News", state: "VA" },
  { iata: "LYH", name: "Lynchburg Regional Airport", city: "Lynchburg", state: "VA" },
  // Washington
  { iata: "SEA", name: "Seattle-Tacoma International Airport", city: "Seattle", state: "WA" },
  { iata: "GEG", name: "Spokane International Airport", city: "Spokane", state: "WA" },
  { iata: "YKM", name: "Yakima Air Terminal", city: "Yakima", state: "WA" },
  { iata: "PSC", name: "Tri-Cities Airport", city: "Pasco", state: "WA" },
  { iata: "ALW", name: "Walla Walla Regional Airport", city: "Walla Walla", state: "WA" },
  { iata: "PUW", name: "Pullman-Moscow Regional Airport", city: "Pullman", state: "WA" },
  { iata: "BLI", name: "Bellingham International Airport", city: "Bellingham", state: "WA" },
  { iata: "EAT", name: "Pangborn Memorial Airport", city: "Wenatchee", state: "WA" },
  // West Virginia
  { iata: "CRW", name: "West Virginia International Yeager Airport", city: "Charleston", state: "WV" },
  { iata: "HTS", name: "Tri-State Airport", city: "Huntington", state: "WV" },
  { iata: "CKB", name: "North Central West Virginia Airport", city: "Clarksburg", state: "WV" },
  // Wisconsin
  { iata: "MKE", name: "Milwaukee Mitchell International Airport", city: "Milwaukee", state: "WI" },
  { iata: "MSN", name: "Dane County Regional Airport", city: "Madison", state: "WI" },
  { iata: "GRB", name: "Green Bay Austin Straubel International Airport", city: "Green Bay", state: "WI" },
  { iata: "ATW", name: "Appleton International Airport", city: "Appleton", state: "WI" },
  { iata: "EAU", name: "Chippewa Valley Regional Airport", city: "Eau Claire", state: "WI" },
  // Wyoming
  { iata: "JAC", name: "Jackson Hole Airport", city: "Jackson", state: "WY" },
  { iata: "COD", name: "Yellowstone Regional Airport", city: "Cody", state: "WY" },
  { iata: "CPR", name: "Casper-Natrona County International Airport", city: "Casper", state: "WY" },
  { iata: "GCC", name: "Northeast Wyoming Regional Airport", city: "Gillette", state: "WY" },
  { iata: "RIW", name: "Central Wyoming Regional Airport", city: "Riverton", state: "WY" },
  { iata: "LAR", name: "Laramie Regional Airport", city: "Laramie", state: "WY" },
  { iata: "RKS", name: "Southwest Wyoming Regional Airport", city: "Rock Springs", state: "WY" },
  { iata: "SHR", name: "Sheridan County Airport", city: "Sheridan", state: "WY" },
  { iata: "CYS", name: "Cheyenne Regional Airport", city: "Cheyenne", state: "WY" },
];

const BY_IATA: Map<string, UsAirport> = new Map(
  US_AIRPORTS.map((a) => [a.iata, a]),
);

export function airportByIata(iata: string): UsAirport | null {
  return BY_IATA.get(iata.trim().toUpperCase()) ?? null;
}

// The primary suggested airport per state (first-listed per state = the state's
// main hub). The guaranteed non-null backstop when a ZIP band misses.
const PRIMARY_BY_STATE: Map<string, UsAirport> = (() => {
  const m = new Map<string, UsAirport>();
  for (const a of US_AIRPORTS) if (!m.has(a.state)) m.set(a.state, a);
  return m;
})();

// #441: contiguous ZIP3 bands covering the whole US, each mapped to the NEAREST
// cargo/commercial airport. The FIRST band that contains the ZIP3 wins (a few
// dense metros intentionally overlap a broader band; the earlier entry is the
// better nearest). Any ZIP a band misses falls back to the state primary.
const ZIP3_RANGES: Array<{ lo: number; hi: number; iata: string }> = [
  // Northeast
  { lo: 5, hi: 5, iata: "JFK" }, // Holtsville NY
  { lo: 6, hi: 9, iata: "MIA" }, // PR / USVI -> nearest CONUS cargo gateway
  { lo: 10, hi: 27, iata: "BOS" }, // Massachusetts
  { lo: 28, hi: 29, iata: "PVD" }, // Rhode Island
  { lo: 30, hi: 38, iata: "MHT" }, // New Hampshire
  { lo: 39, hi: 43, iata: "PWM" }, // Maine (south)
  { lo: 44, hi: 49, iata: "BGR" }, // Maine (north)
  { lo: 50, hi: 59, iata: "BTV" }, // Vermont
  { lo: 60, hi: 67, iata: "BDL" }, // Connecticut
  { lo: 68, hi: 69, iata: "HPN" }, // Fairfield County CT -> NY-area (Old Greenwich 068)
  { lo: 70, hi: 79, iata: "EWR" }, // North NJ
  { lo: 80, hi: 84, iata: "ACY" }, // South NJ
  { lo: 85, hi: 89, iata: "EWR" }, // NJ
  { lo: 90, hi: 99, iata: "JFK" }, // APO/AE Europe -> east coast gateway
  // New York
  { lo: 100, hi: 104, iata: "JFK" }, // NYC + Westchester
  { lo: 105, hi: 109, iata: "HPN" }, // Hudson Valley south
  { lo: 110, hi: 114, iata: "JFK" }, // Queens/Nassau
  { lo: 115, hi: 119, iata: "ISP" }, // Suffolk / Long Island east
  { lo: 120, hi: 129, iata: "ALB" }, // Capital District
  { lo: 130, hi: 139, iata: "SYR" }, // Central NY
  { lo: 140, hi: 143, iata: "BUF" }, // Buffalo
  { lo: 144, hi: 149, iata: "ROC" }, // Rochester / Southern Tier west
  // Pennsylvania
  { lo: 150, hi: 168, iata: "PIT" }, // Western/central-west PA
  { lo: 169, hi: 178, iata: "MDT" }, // Harrisburg region
  { lo: 179, hi: 181, iata: "ABE" }, // Lehigh Valley
  { lo: 182, hi: 188, iata: "AVP" }, // Scranton / Wilkes-Barre
  { lo: 189, hi: 196, iata: "PHL" }, // Philadelphia metro
  { lo: 197, hi: 199, iata: "ILG" }, // Delaware
  // DC / Mid-Atlantic
  { lo: 200, hi: 205, iata: "DCA" }, // DC
  { lo: 206, hi: 214, iata: "BWI" }, // Maryland
  { lo: 215, hi: 219, iata: "SBY" }, // Eastern Shore MD
  { lo: 220, hi: 223, iata: "IAD" }, // Northern VA
  { lo: 224, hi: 232, iata: "RIC" }, // Central VA
  { lo: 233, hi: 239, iata: "ORF" }, // Tidewater VA
  { lo: 240, hi: 244, iata: "ROA" }, // SW VA
  { lo: 245, hi: 246, iata: "LYH" }, // Lynchburg
  // West Virginia
  { lo: 247, hi: 259, iata: "CRW" }, // Charleston WV
  { lo: 260, hi: 262, iata: "CKB" }, // Clarksburg
  { lo: 263, hi: 268, iata: "HTS" }, // Huntington
  // North Carolina
  { lo: 270, hi: 274, iata: "GSO" }, // Piedmont Triad
  { lo: 275, hi: 279, iata: "RDU" }, // Raleigh-Durham
  { lo: 280, hi: 282, iata: "CLT" }, // Charlotte (Salisbury 281)
  { lo: 283, hi: 284, iata: "FAY" }, // Fayetteville
  { lo: 285, hi: 285, iata: "ILM" }, // Wilmington
  { lo: 286, hi: 287, iata: "AVL" }, // Western NC
  { lo: 288, hi: 289, iata: "GSO" },
  // South Carolina
  { lo: 290, hi: 293, iata: "CAE" }, // Columbia
  { lo: 294, hi: 294, iata: "CHS" }, // Charleston
  { lo: 295, hi: 296, iata: "MYR" }, // Florence / Myrtle Beach
  { lo: 297, hi: 299, iata: "GSP" }, // Greenville / Spartanburg
  // Georgia
  { lo: 300, hi: 305, iata: "ATL" }, // Atlanta
  { lo: 306, hi: 306, iata: "AGS" },
  { lo: 307, hi: 307, iata: "TRI" }, // NW GA -> Chattanooga/Tri-Cities region
  { lo: 308, hi: 309, iata: "AGS" }, // Augusta
  { lo: 310, hi: 312, iata: "ATL" }, // Macon area
  { lo: 313, hi: 314, iata: "SAV" }, // Savannah
  { lo: 315, hi: 316, iata: "VLD" }, // Valdosta
  { lo: 317, hi: 319, iata: "ABY" }, // Albany GA
  // Florida
  { lo: 320, hi: 322, iata: "JAX" }, // Jacksonville
  { lo: 323, hi: 323, iata: "TLH" }, // Tallahassee
  { lo: 324, hi: 324, iata: "ECP" }, // Panama City
  { lo: 325, hi: 325, iata: "PNS" }, // Pensacola
  { lo: 326, hi: 326, iata: "GNV" }, // Gainesville
  { lo: 327, hi: 329, iata: "MCO" }, // Orlando
  { lo: 330, hi: 334, iata: "MIA" }, // Miami / Fort Lauderdale / West Palm
  { lo: 335, hi: 337, iata: "TPA" }, // Tampa / St. Petersburg
  { lo: 338, hi: 339, iata: "RSW" }, // Fort Myers / Naples region
  { lo: 340, hi: 340, iata: "MIA" }, // APO/AA Americas -> Miami gateway
  { lo: 341, hi: 342, iata: "RSW" }, // Naples 341 -> Fort Myers
  { lo: 343, hi: 344, iata: "GNV" },
  { lo: 346, hi: 346, iata: "TPA" },
  { lo: 347, hi: 349, iata: "MCO" },
  // Alabama
  { lo: 350, hi: 355, iata: "BHM" }, // Birmingham
  { lo: 356, hi: 358, iata: "HSV" }, // Huntsville
  { lo: 359, hi: 359, iata: "BHM" },
  { lo: 360, hi: 362, iata: "MGM" }, // Montgomery
  { lo: 363, hi: 364, iata: "DHN" }, // Dothan
  { lo: 365, hi: 366, iata: "MOB" }, // Mobile
  { lo: 367, hi: 369, iata: "MGM" },
  // Tennessee
  { lo: 370, hi: 372, iata: "BNA" }, // Nashville
  { lo: 373, hi: 374, iata: "CHA" }, // Chattanooga
  { lo: 375, hi: 375, iata: "MEM" }, // Memphis
  { lo: 376, hi: 376, iata: "TRI" }, // Tri-Cities
  { lo: 377, hi: 379, iata: "TYS" }, // Knoxville
  { lo: 380, hi: 381, iata: "MEM" }, // Memphis
  { lo: 382, hi: 385, iata: "BNA" }, // Upper Cumberland
  // Mississippi
  { lo: 386, hi: 389, iata: "MEM" }, // North MS near Memphis
  { lo: 390, hi: 392, iata: "JAN" }, // Jackson
  { lo: 393, hi: 393, iata: "JAN" }, // Meridian
  { lo: 394, hi: 396, iata: "GPT" }, // Hattiesburg / Gulf Coast
  { lo: 397, hi: 397, iata: "JAN" },
  { lo: 398, hi: 399, iata: "ATL" }, // extreme SW GA -> Atlanta
  // Kentucky
  { lo: 400, hi: 409, iata: "LEX" }, // Central KY
  { lo: 410, hi: 412, iata: "SDF" }, // Louisville
  { lo: 413, hi: 418, iata: "LEX" }, // Eastern KY
  { lo: 420, hi: 422, iata: "PAH" }, // Western KY
  { lo: 423, hi: 424, iata: "OWB" }, // Owensboro
  { lo: 425, hi: 427, iata: "LEX" },
  // Ohio
  { lo: 430, hi: 433, iata: "CMH" }, // Columbus
  { lo: 434, hi: 436, iata: "TOL" }, // Toledo
  { lo: 437, hi: 438, iata: "CMH" }, // Zanesville
  { lo: 439, hi: 439, iata: "CAK" }, // Steubenville
  { lo: 440, hi: 444, iata: "CLE" }, // Cleveland
  { lo: 445, hi: 449, iata: "CAK" }, // Youngstown / Canton
  { lo: 450, hi: 455, iata: "CVG" }, // Cincinnati
  { lo: 456, hi: 459, iata: "DAY" }, // Dayton
  // Indiana
  { lo: 460, hi: 466, iata: "IND" }, // Indianapolis
  { lo: 467, hi: 468, iata: "FWA" }, // Fort Wayne
  { lo: 469, hi: 469, iata: "IND" },
  { lo: 470, hi: 471, iata: "CVG" }, // SE IN near Cincinnati
  { lo: 472, hi: 474, iata: "IND" }, // Bloomington
  { lo: 475, hi: 477, iata: "EVV" }, // Evansville
  { lo: 478, hi: 479, iata: "FWA" }, // NE IN
  // Michigan
  { lo: 480, hi: 485, iata: "DTW" }, // Detroit metro
  { lo: 486, hi: 487, iata: "MBS" }, // Saginaw
  { lo: 488, hi: 489, iata: "LAN" }, // Lansing
  { lo: 490, hi: 491, iata: "AZO" }, // Kalamazoo
  { lo: 492, hi: 492, iata: "FNT" }, // Jackson MI
  { lo: 493, hi: 495, iata: "GRR" }, // Grand Rapids
  { lo: 496, hi: 497, iata: "TVC" }, // Traverse City / northern LP
  { lo: 498, hi: 499, iata: "MQT" }, // Upper Peninsula
  // Iowa
  { lo: 500, hi: 508, iata: "DSM" }, // Des Moines
  { lo: 509, hi: 516, iata: "SUX" }, // Sioux City region
  { lo: 520, hi: 522, iata: "DBQ" }, // Dubuque
  { lo: 523, hi: 524, iata: "CID" }, // Cedar Rapids
  { lo: 525, hi: 525, iata: "DSM" },
  { lo: 526, hi: 528, iata: "CID" }, // Eastern Iowa / Quad Cities side
  // Wisconsin
  { lo: 530, hi: 534, iata: "MKE" }, // Milwaukee
  { lo: 535, hi: 539, iata: "MSN" }, // Madison
  { lo: 540, hi: 544, iata: "GRB" }, // Green Bay / NE WI
  { lo: 545, hi: 545, iata: "ATW" }, // Appleton
  { lo: 546, hi: 547, iata: "EAU" }, // Eau Claire / western WI
  { lo: 548, hi: 549, iata: "GRB" }, // northern WI
  // Minnesota
  { lo: 550, hi: 555, iata: "MSP" }, // Twin Cities
  { lo: 556, hi: 558, iata: "DLH" }, // Duluth / northern MN
  { lo: 559, hi: 559, iata: "RST" }, // Rochester MN
  { lo: 560, hi: 564, iata: "MSP" }, // southern MN
  { lo: 565, hi: 567, iata: "FAR" }, // NW MN near Fargo
  // South Dakota
  { lo: 570, hi: 572, iata: "FSD" }, // Sioux Falls
  { lo: 573, hi: 574, iata: "PIR" }, // central SD -> Pierre
  { lo: 575, hi: 577, iata: "RAP" }, // Rapid City / western SD
  // North Dakota
  { lo: 580, hi: 581, iata: "FAR" }, // Fargo
  { lo: 582, hi: 583, iata: "GFK" }, // Grand Forks
  { lo: 584, hi: 586, iata: "BIS" }, // Bismarck
  { lo: 587, hi: 587, iata: "MOT" }, // Minot
  { lo: 588, hi: 588, iata: "ISN" }, // Williston
  // Montana
  { lo: 590, hi: 591, iata: "BIL" }, // Billings
  { lo: 592, hi: 592, iata: "GTF" }, // NE MT
  { lo: 593, hi: 593, iata: "BIL" }, // Miles City
  { lo: 594, hi: 594, iata: "GTF" }, // Great Falls
  { lo: 595, hi: 595, iata: "HLN" }, // Helena
  { lo: 596, hi: 596, iata: "BIL" },
  { lo: 597, hi: 597, iata: "BZN" }, // Bozeman
  { lo: 598, hi: 598, iata: "MSO" }, // Missoula
  { lo: 599, hi: 599, iata: "FCA" }, // Kalispell
  // Illinois
  { lo: 600, hi: 609, iata: "ORD" }, // Chicago metro
  { lo: 610, hi: 614, iata: "MLI" }, // Rockford / Quad Cities side
  { lo: 615, hi: 616, iata: "PIA" }, // Peoria
  { lo: 617, hi: 617, iata: "BMI" }, // Bloomington
  { lo: 618, hi: 619, iata: "CMI" }, // Champaign
  { lo: 620, hi: 624, iata: "STL" }, // Metro East -> St. Louis
  { lo: 625, hi: 627, iata: "SPI" }, // Springfield IL
  { lo: 628, hi: 629, iata: "STL" }, // Southern IL -> St. Louis
  // Missouri
  { lo: 630, hi: 639, iata: "STL" }, // St. Louis
  { lo: 640, hi: 641, iata: "MCI" }, // Kansas City
  { lo: 644, hi: 646, iata: "MCI" }, // St. Joseph
  { lo: 647, hi: 648, iata: "SGF" }, // SW MO
  { lo: 650, hi: 653, iata: "COU" }, // central MO / Jefferson City
  { lo: 654, hi: 658, iata: "SGF" }, // Springfield / Joplin region
  // Kansas
  { lo: 660, hi: 662, iata: "MCI" }, // Kansas City KS
  { lo: 664, hi: 669, iata: "MHK" }, // Topeka / Manhattan region
  { lo: 670, hi: 676, iata: "ICT" }, // Wichita / central KS
  { lo: 677, hi: 679, iata: "GCK" }, // western KS -> Garden City
  // Nebraska
  { lo: 680, hi: 681, iata: "OMA" }, // Omaha
  { lo: 683, hi: 685, iata: "LNK" }, // Lincoln
  { lo: 686, hi: 693, iata: "GRI" }, // central / western NE -> Grand Island
  // Louisiana
  { lo: 700, hi: 704, iata: "MSY" }, // New Orleans
  { lo: 705, hi: 705, iata: "LFT" }, // Lafayette
  { lo: 706, hi: 706, iata: "LCH" }, // Lake Charles
  { lo: 707, hi: 708, iata: "BTR" }, // Baton Rouge
  { lo: 710, hi: 711, iata: "SHV" }, // Shreveport
  { lo: 712, hi: 712, iata: "MLU" }, // Monroe
  { lo: 713, hi: 714, iata: "AEX" }, // Alexandria
  // Arkansas
  { lo: 716, hi: 718, iata: "LIT" }, // south AR
  { lo: 719, hi: 719, iata: "TXK" }, // Texarkana side
  { lo: 720, hi: 726, iata: "LIT" }, // Little Rock / Jonesboro / Batesville
  { lo: 727, hi: 727, iata: "XNA" }, // NW Arkansas -> Bentonville/Fayetteville
  { lo: 728, hi: 729, iata: "FSM" }, // Fort Smith
  // Oklahoma
  { lo: 730, hi: 731, iata: "OKC" }, // Oklahoma City
  { lo: 733, hi: 733, iata: "OKC" }, // NW OKC
  { lo: 734, hi: 735, iata: "LAW" }, // Lawton / SW OK
  { lo: 736, hi: 737, iata: "OKC" }, // Clinton / Enid
  { lo: 738, hi: 739, iata: "AMA" }, // Woodward -> Amarillo closer
  { lo: 740, hi: 745, iata: "TUL" }, // Tulsa / McAlester
  { lo: 746, hi: 746, iata: "OKC" }, // Ponca City
  { lo: 747, hi: 749, iata: "TUL" }, // Poteau / Durant side
  // Texas
  { lo: 750, hi: 754, iata: "DFW" }, // Dallas
  { lo: 755, hi: 755, iata: "TXK" }, // Texarkana TX
  { lo: 756, hi: 756, iata: "GGG" }, // Longview
  { lo: 757, hi: 759, iata: "TYR" }, // Tyler / Lufkin side
  { lo: 760, hi: 763, iata: "DFW" }, // Fort Worth / Denton / Wichita Falls
  { lo: 764, hi: 767, iata: "ACT" }, // Waco / Temple
  { lo: 768, hi: 768, iata: "ABI" }, // Abilene
  { lo: 769, hi: 769, iata: "MAF" }, // Midland / Odessa
  { lo: 770, hi: 777, iata: "IAH" }, // Houston metro / Beaumont side
  { lo: 778, hi: 778, iata: "CLL" }, // Bryan / College Station
  { lo: 779, hi: 779, iata: "VCT" }, // Victoria
  { lo: 780, hi: 782, iata: "SAT" }, // San Antonio (Leon Valley 782)
  { lo: 783, hi: 784, iata: "CRP" }, // Corpus Christi
  { lo: 785, hi: 785, iata: "MFE" }, // McAllen
  { lo: 786, hi: 787, iata: "AUS" }, // Austin
  { lo: 788, hi: 788, iata: "SAT" }, // SW of San Antonio
  { lo: 789, hi: 789, iata: "AUS" }, // Del Rio side
  { lo: 790, hi: 791, iata: "AMA" }, // Amarillo
  { lo: 792, hi: 794, iata: "LBB" }, // Lubbock
  { lo: 795, hi: 796, iata: "ABI" }, // Abilene
  { lo: 797, hi: 797, iata: "MAF" }, // Midland
  { lo: 798, hi: 799, iata: "ELP" }, // El Paso
  // Colorado
  { lo: 800, hi: 807, iata: "DEN" }, // Denver metro / NE plains
  { lo: 808, hi: 809, iata: "COS" }, // Colorado Springs
  { lo: 810, hi: 810, iata: "PUB" }, // Pueblo
  { lo: 811, hi: 812, iata: "PUB" }, // Alamosa / Salida side
  { lo: 813, hi: 813, iata: "DRO" }, // Durango / SW CO
  { lo: 814, hi: 814, iata: "MTJ" }, // Montrose / Gunnison
  { lo: 815, hi: 816, iata: "GJT" }, // Grand Junction / Glenwood
  // Wyoming
  { lo: 820, hi: 820, iata: "CYS" }, // Cheyenne
  { lo: 821, hi: 821, iata: "RKS" }, // west
  { lo: 822, hi: 822, iata: "LAR" }, // Wheatland / Laramie side
  { lo: 823, hi: 823, iata: "RKS" }, // Rawlins -> Rock Springs
  { lo: 824, hi: 824, iata: "RIW" }, // Worland / Riverton
  { lo: 825, hi: 826, iata: "CPR" }, // Casper
  { lo: 827, hi: 827, iata: "GCC" }, // Gillette
  { lo: 828, hi: 828, iata: "SHR" }, // Sheridan
  { lo: 829, hi: 831, iata: "RKS" }, // Rock Springs / SW WY
  // Idaho
  { lo: 832, hi: 832, iata: "IDA" }, // Idaho Falls region
  { lo: 833, hi: 833, iata: "SUN" }, // Sun Valley / Twin Falls side
  { lo: 834, hi: 834, iata: "IDA" }, // Idaho Falls
  { lo: 835, hi: 835, iata: "LWS" }, // Lewiston
  { lo: 836, hi: 837, iata: "BOI" }, // Boise
  { lo: 838, hi: 838, iata: "GEG" }, // northern ID panhandle -> Spokane
  // Utah
  { lo: 840, hi: 841, iata: "SLC" }, // Salt Lake City
  { lo: 842, hi: 844, iata: "OGD" }, // Ogden / northern UT
  { lo: 845, hi: 846, iata: "PVU" }, // Provo / Orem
  { lo: 847, hi: 847, iata: "SGU" }, // St. George / southern UT
  // Arizona
  { lo: 850, hi: 853, iata: "PHX" }, // Phoenix (San Tan Valley 851)
  { lo: 855, hi: 855, iata: "PHX" }, // Globe
  { lo: 856, hi: 857, iata: "TUS" }, // Tucson
  { lo: 859, hi: 861, iata: "FLG" }, // Flagstaff / Show Low
  { lo: 863, hi: 863, iata: "PRC" }, // Prescott
  { lo: 864, hi: 864, iata: "YUM" }, // Yuma
  { lo: 865, hi: 865, iata: "PHX" }, // northern AZ fallback
  // New Mexico
  { lo: 870, hi: 872, iata: "ABQ" }, // Albuquerque
  { lo: 873, hi: 874, iata: "FMN" }, // Gallup / Farmington
  { lo: 875, hi: 875, iata: "ABQ" }, // Santa Fe metro
  { lo: 877, hi: 877, iata: "SAF" }, // Las Vegas NM / Santa Fe
  { lo: 878, hi: 879, iata: "ABQ" }, // Socorro / T or C
  { lo: 880, hi: 880, iata: "ELP" }, // Las Cruces -> El Paso
  { lo: 881, hi: 882, iata: "ROW" }, // Clovis / Roswell
  { lo: 883, hi: 884, iata: "ABQ" }, // Tucumcari / NE NM
  { lo: 885, hi: 885, iata: "ELP" }, // El Paso TX enclave
  // Nevada
  { lo: 889, hi: 891, iata: "LAS" }, // Las Vegas
  { lo: 893, hi: 893, iata: "EKO" }, // Ely / east-central NV
  { lo: 894, hi: 895, iata: "RNO" }, // Reno
  { lo: 897, hi: 897, iata: "RNO" }, // Carson City
  { lo: 898, hi: 898, iata: "EKO" }, // Elko
  // California
  { lo: 900, hi: 908, iata: "LAX" }, // Los Angeles core
  { lo: 909, hi: 909, iata: "ONT" }, // San Bernardino area
  { lo: 910, hi: 916, iata: "BUR" }, // Pasadena / San Fernando Valley
  { lo: 917, hi: 918, iata: "ONT" }, // Ontario / Inland Empire
  { lo: 919, hi: 921, iata: "SAN" }, // San Diego
  { lo: 922, hi: 922, iata: "PSP" }, // Palm Springs / Coachella
  { lo: 923, hi: 925, iata: "ONT" }, // Riverside / San Bernardino
  { lo: 926, hi: 928, iata: "SNA" }, // Orange County
  { lo: 930, hi: 931, iata: "SBA" }, // Oxnard / Santa Barbara
  { lo: 932, hi: 933, iata: "BFL" }, // Bakersfield
  { lo: 934, hi: 934, iata: "SBA" }, // Santa Barbara coast
  { lo: 935, hi: 935, iata: "BFL" }, // Mojave / Ridgecrest
  { lo: 936, hi: 938, iata: "FAT" }, // Fresno
  { lo: 939, hi: 939, iata: "MRY" }, // Salinas / Monterey
  { lo: 940, hi: 941, iata: "SFO" }, // San Francisco
  { lo: 942, hi: 943, iata: "SMF" }, // Sacramento area
  { lo: 944, hi: 944, iata: "SFO" }, // San Mateo
  { lo: 945, hi: 948, iata: "OAK" }, // Oakland / East Bay
  { lo: 949, hi: 949, iata: "SMF" }, // North Bay
  { lo: 950, hi: 951, iata: "SJC" }, // San Jose
  { lo: 952, hi: 954, iata: "SMF" }, // Stockton / Modesto / Santa Rosa
  { lo: 955, hi: 955, iata: "RDD" }, // far north
  { lo: 956, hi: 959, iata: "SMF" }, // Sacramento / Marysville
  { lo: 960, hi: 960, iata: "RDD" }, // Redding
  { lo: 961, hi: 961, iata: "RNO" }, // Lake Tahoe / eastern -> Reno
  // Hawaii / Pacific
  { lo: 967, hi: 969, iata: "HNL" },
  // Oregon
  { lo: 970, hi: 972, iata: "PDX" }, // Portland
  { lo: 973, hi: 974, iata: "EUG" }, // Salem / Eugene
  { lo: 975, hi: 975, iata: "MFR" }, // Medford / Roseburg
  { lo: 976, hi: 976, iata: "LMT" }, // Klamath Falls
  { lo: 977, hi: 978, iata: "RDM" }, // Bend / central / Pendleton
  { lo: 979, hi: 979, iata: "OTH" }, // coastal / North Bend
  // Washington
  { lo: 980, hi: 985, iata: "SEA" }, // Seattle / Tacoma / Everett
  { lo: 986, hi: 986, iata: "PDX" }, // Vancouver WA -> Portland
  { lo: 988, hi: 988, iata: "EAT" }, // Wenatchee
  { lo: 989, hi: 989, iata: "YKM" }, // Yakima
  { lo: 990, hi: 992, iata: "GEG" }, // Spokane
  { lo: 993, hi: 993, iata: "PSC" }, // Pasco / Tri-Cities
  { lo: 994, hi: 994, iata: "LWS" }, // Clarkston -> Lewiston
  // Alaska
  { lo: 995, hi: 996, iata: "ANC" }, // Anchorage
  { lo: 997, hi: 997, iata: "FAI" }, // Fairbanks
  { lo: 998, hi: 999, iata: "JNU" }, // Southeast AK
];

// Suggest a nearest airport from the client's ZIP (preferred) then state.
// Returns null only when neither yields a match (caller then requires the
// client to search + pick). The suggestion is NEVER auto-applied - see #431.
export function suggestAirport(input: {
  zip?: string | null;
  state?: string | null;
}): UsAirport | null {
  const zip = (input.zip ?? "").trim();
  if (zip.length >= 3) {
    const z3 = Number(zip.slice(0, 3));
    if (Number.isFinite(z3)) {
      for (const r of ZIP3_RANGES) {
        if (z3 >= r.lo && z3 <= r.hi) {
          const a = BY_IATA.get(r.iata);
          if (a) return a;
          break; // matched band but airport missing (data bug) -> state fallback
        }
      }
    }
  }
  const state = (input.state ?? "").trim().toUpperCase();
  if (state) return PRIMARY_BY_STATE.get(state) ?? null;
  return null;
}

// Search the airport list by IATA, city, or name (case-insensitive substring).
export function searchAirports(query: string, limit = 12): UsAirport[] {
  const q = query.trim().toLowerCase();
  if (!q) return US_AIRPORTS.slice(0, limit);
  const out: UsAirport[] = [];
  for (const a of US_AIRPORTS) {
    if (
      a.iata.toLowerCase().includes(q) ||
      a.city.toLowerCase().includes(q) ||
      a.name.toLowerCase().includes(q) ||
      a.state.toLowerCase() === q
    ) {
      out.push(a);
      if (out.length >= limit) break;
    }
  }
  return out;
}
